'use client'

import React, { useEffect, useMemo } from 'react'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIInput from '@mui/material/TextField'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIStack from '@mui/material/Stack'
import { CalendarViewWeek } from '@mui/icons-material'

const extractEmailFromString = (value: string): string | null => {
  if (!value) return null

  // Simple "find an email anywhere in the string" regex
  const match = value.match(/[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+/)

  return match ? match[0].trim() : null
}

export const RecipientsInput = ({
  label,
  recipients,
  removeRecipients,
  setRecipients,
  inputValue,
  setInputValue,
  field = 'to',
  onValidityChange,
  canSend,
  setCanSend,
  testData = false
}: RecipientsInputProps) => {
  const theme = useTheme()

  const isBasicEmail = (value: string) => {
    const extracted = extractEmailFromString(value)
    if (!extracted) return false

    const str = extracted.trim()
    const at = str.indexOf('@')
    const dot = str.indexOf('.', at + 1)
    return at > 0 && dot > at + 1 && dot < str.length - 1
  }

  const allRecipients = useMemo(
    () => [...(recipients?.clients || []), ...(recipients?.contacts || []), ...(recipients?.organisations || [])],
    [recipients]
  )

  const hasAnyRecipients = allRecipients.length > 0
  const hasInvalidCommitted = allRecipients.some((r) => !isBasicEmail(r.email))

  const hasDraft = !!inputValue?.trim()
  const draftInvalid = hasDraft && !isBasicEmail(inputValue)

  const localValid =
    field === 'to' ? hasAnyRecipients && !hasInvalidCommitted && !draftInvalid : !hasInvalidCommitted && !draftInvalid

  useEffect(() => {
    if (testData) return
    onValidityChange?.({ field, valid: localValid })
    setCanSend?.(localValid)
  }, [field, localValid, onValidityChange, setCanSend, testData])

  const commitPending = () => {
    if (testData) return
    const raw = (inputValue || '').trim()
    if (!raw) return

    const email = extractEmailFromString(raw) || raw
    setRecipients({ recipientType: 'contacts', email })
    setInputValue('')
  }

  return (
    <MUIStack
      direction="row"
      alignItems="baseline"
      flexWrap="wrap"
      data-testid="RecipientsInput"
      sx={{
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        margin: '1rem 1rem 0',
        width: 'calc(100% - 2rem)',
        height: 'auto',
        minHeight: '36px'
      }}
    >
      <MUIInputLabel
        style={{
          color: theme.palette.grey[400],
          marginRight: '0.5rem',
          width: '2rem',
          backgroundColor: draftInvalid ? theme.palette.error.main : undefined,
          borderRadius: draftInvalid ? '0.25rem' : undefined,
          padding: draftInvalid ? '0.125rem 0.25rem' : undefined
        }}
      >
        {label}
      </MUIInputLabel>

      {testData !== true &&
        allRecipients.map((recipient: { email: string }) => {
          const valid = isBasicEmail(recipient.email)
          const bg = valid ? theme.palette.secondary.light : theme.palette.error.main
          const fg = valid ? theme.palette.primary.contrastText : theme.palette.error.contrastText
          return (
            <MUIInputLabel
              key={recipient.email}
              style={{
                backgroundColor: bg,
                borderRadius: '1rem',
                color: fg,
                marginRight: '0.5rem',
                width: 'fit-content',
                padding: '0.25rem 0.75rem',
                display: 'flex',
                alignItems: 'center'
              }}
              aria-invalid={!valid}
              title={!valid ? 'Invalid email' : undefined}
            >
              {recipient.email}
              <MUIButton
                onClick={() => removeRecipients({ email: recipient.email })}
                variant="text"
                sx={{
                  width: '1rem',
                  height: '1rem',
                  p: 0,
                  margin: '0 0 0.125rem 0.5rem',
                  cursor: 'pointer',
                  minWidth: '1rem !important'
                }}
                aria-label={`Remove ${recipient.email}`}
              >
                <CloseIcon
                  sx={{
                    borderRadius: '50%',
                    border: `0.5px solid ${fg}`,
                    height: '1rem',
                    width: '1rem',
                    p: '2px',
                    mt: '2px',
                    cursor: 'pointer',
                    color: fg,
                    strokeWidth: '3px',
                    strokeOpacity: '1.0'
                  }}
                />
              </MUIButton>
            </MUIInputLabel>
          )
        })}

      <MUIBox sx={{ flexGrow: 1, minWidth: '150px', alignSelf: 'flex-start' }}>
        <MUIInput
          fullWidth
          variant="standard"
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (testData) return
            setInputValue(e.target.value)
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (testData) return
            if (['Enter', ',', ' ', 'Tab', ';'].includes(e.key)) {
              e.preventDefault()
              commitPending()
            }
          }}
          onBlur={commitPending}
          error={!!draftInvalid}
          sx={{
            borderBottom: 'none',
            width: '100%',
            '.MuiFormControl-root': { width: '100%' },
            '.MuiInputBase-root': {
              width: '100%',
              marginBottom: 0,
              '&:before, &:after': { display: 'none' }
            }
          }}
        />
      </MUIBox>
    </MUIStack>
  )
}

type RecipientsInputProps = {
  label: string
  recipients: {
    clients?: { email: string }[]
    contacts?: { email: string }[]
    organisations?: { email: string }[]
  }
  removeRecipients: (recipient: { email: string }) => void
  setRecipients: (recipient: { recipientType: string; email: string }) => void
  inputValue: string
  setInputValue: (value: string) => void
  field?: 'to' | 'cc' | 'bcc'
  onValidityChange?: (state: { field: 'to' | 'cc' | 'bcc'; valid: boolean }) => void
  canSend?: boolean
  setCanSend?: (value: boolean) => void
  testData?: boolean
}
